// InterviewVoiceOnlyActive.tsx
//
// Active-session view. Mirrors the live-call layout from VideoContainer
// (lines 712–903): outer dark card with a two-column flex row — the left
// column holds the visual (Blob, replacing the camera tile) and a controls
// footer with timer + roleplay title + End button + transcript toggle; the
// right column slides in the existing TranscriptionPanel.
//
// AIPersonaOverlay is intentionally omitted — voice-only mode doesn't show
// the AI avatar tile.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConversationProvider, useConversation } from "@elevenlabs/react";
import { Clock as ClockIcon, Phone as PhoneIcon, Loader2 as Loader2Icon, Settings as SettingsIcon } from "lucide-react";
import ReactiveOrb from "@component/reactive-orb";
import EnhancedAudioVisualizer from "@component/enhanced-audio-visualizer";
import LiveStatusBar, { type LiveStatusState } from "@component/live-status-bar";
import TranscriptionPanel from "@component/transcription-panel";
import TranscriptionToggleButton from "@component/transcription-toggle-button";
import SettingsModal from "@component/interview-settings-modal";
import {
    apiGetSession,
    apiGetVoiceSignedUrl,
    apiVoiceConnect,
    apiCompleteSession,
    apiStartSession,
    apiPreflightStt,
} from "@service/training";
import FeedbackPopup from "@component/feedback-popup";
import { stripAudioTags } from "@util/training-feedback-adapter";
import { stripSpeechTags } from "@util/string";

type Phase = "preflight" | "idle" | "connecting" | "live" | "analyzing" | "feedback" | "error";
type TranscriptMessage = { source: "user" | "ai" | "divider"; message: string };

interface Props {
    onDeclined?: () => void;
    language?: string;
    gender?: string;
}

export default function InterviewVoiceOnlyActive({ onDeclined, language, gender }: Props) {
    return (
        <ConversationProvider>
            <InterviewVoiceOnlyActiveInner onDeclined={onDeclined} language={language} gender={gender} />
        </ConversationProvider>
    );
}

function InterviewVoiceOnlyActiveInner({ onDeclined, language, gender }: Props) {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [phase, setPhase] = useState<Phase>("preflight");
    const preflightVolumeRef = useRef(0);
    const [preflightRecording, setPreflightRecording] = useState(false);
    const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
    const [waitingForFirstMessage, setWaitingForFirstMessage] = useState(false);
    const [showTranscript, setShowTranscript] = useState<boolean>(true);
    const [sessionTitle, setSessionTitle] = useState<string>("");
    const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
    const [isEndingCall, setIsEndingCall] = useState<boolean>(false);
    const [showSettings, setShowSettings] = useState(false);
    const [selectedMicId, setSelectedMicId] = useState("");
    const [selectedSpeakerId, setSelectedSpeakerId] = useState("");
    const [employeeEmail, setEmployeeEmail] = useState("");
    const [campaignName, setCampaignName] = useState("");
    const conversationIdRef = useRef("");
    const sessionStartTimeRef = useRef(Date.now());

    const appendToTranscriptPanel = (message: TranscriptMessage) => {
        setTranscript((prev) => [...prev, message]);
    };

    const conversation = useConversation({
        onConnect: async ({ conversationId }) => {
            if (!token) return;
            conversationIdRef.current = conversationId;
            sessionStartTimeRef.current = Date.now();
            await apiVoiceConnect(token, conversationId);
            setPhase("live");
        },
        onDisconnect: async () => {
            if (!token) return;
            setPhase("analyzing");
            try {
                await apiCompleteSession(token);
            } catch {
                // best-effort — proceed to feedback regardless
            }
            setPhase("feedback");
        },
        onMessage: ({ message, source }) => {
            setWaitingForFirstMessage(false);
            const cleaned = stripSpeechTags(message);
            appendToTranscriptPanel({ source, message: cleaned });
        },
        onError: () => setPhase("error"),
    });

    // Keep a ref to the LAST live conversation so the teardown below always has a
    // valid session to end. Updated in an effect (not during render) and only while
    // "live", so the cleanup on leaving "live" reads the session that was active —
    // even if the hook replaces/clears `conversation` on disconnect (Jack review).
    const conversationRef = useRef(conversation);
    useEffect(() => {
        if (phase === "live" && conversation) {
            conversationRef.current = conversation;
        }
    }, [phase, conversation]);

    // Release the microphone / close the ElevenLabs session whenever the call
    // leaves the "live" phase — manual "End call", natural completion → rating,
    // error, OR unmount (navigate away / browser back). On a server-initiated
    // disconnect the SDK can keep the local mic open, which left the Chrome
    // recording indicator on at the rating screen (#2607). endSession() is
    // best-effort and idempotent — safe to call even if already disconnected.
    useEffect(() => {
        if (phase !== "live") return;
        return () => {
            const conv = conversationRef.current;
            if (conv && typeof conv.endSession === "function") {
                Promise.resolve(conv.endSession()).catch(() => {});
            }
        };
    }, [phase]);

    async function handleEnd() {
        if (isEndingCall) return;
        setIsEndingCall(true);
        await conversation.endSession();
        // onDisconnect fires automatically → setPhase("analyzing")
        //   → apiCompleteSession → setPhase("feedback")
    }

    const autoStartedRef = useRef(false);
    useEffect(() => {
        if (autoStartedRef.current) return;
        if (!token) return;
        autoStartedRef.current = true;
        runPreflight(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    async function connectEL(token: string) {
        try {
            const result = await apiGetVoiceSignedUrl(token);
            if (!result) { setPhase("error"); return; }
            setPhase("connecting");
            await conversation.startSession({ signedUrl: result.signed_url });
        } catch {
            setPhase("error");
        }
    }

    async function runPreflight(token: string) {
        const setPreflightVolume = (v: number) => { preflightVolumeRef.current = v; };

        appendToTranscriptPanel({ source: "ai", message: getGreetingText(language) });
        await playAudioUrl(getPreflightAudioUrl(language, gender), setPreflightVolume);
        preflightVolumeRef.current = 0;

        // Record reply with VAD (max 6s)
        let blob: Blob | null = null;
        let mimeType = "audio/webm";
        let stream: MediaStream | null = null;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setPreflightRecording(true);
            const result = await recordUntilSilence(stream, 6000, setPreflightVolume);
            blob = result.blob;
            mimeType = result.mimeType;
        } catch {
            await apiStartSession(token);
            await connectEL(token);
            return;
        } finally {
            setPreflightRecording(false);
            stream?.getTracks().forEach((t) => t.stop());
            preflightVolumeRef.current = 0;
        }

        const stt = await apiPreflightStt(token, blob, mimeType);

        if (!stt) {
            // fail open — STT failed
            await apiStartSession(token);
            await connectEL(token);
            return;
        }

        if (!stt.answered_yes) {
            onDeclined?.();
            return;
        }

        // User confirmed — prefetch signed URL during ringing so WebRTC starts the moment ring ends
        appendToTranscriptPanel({ source: "divider", message: "Roleplay Started" });
        setWaitingForFirstMessage(true);
        const [, signedUrl] = await Promise.all([
            playRinging(),
            (async () => { await apiStartSession(token); return apiGetVoiceSignedUrl(token); })(),
        ]);
        if (!signedUrl) { setPhase("error"); return; }
        setPhase("connecting");
        await conversation.startSession({ signedUrl: signedUrl.signed_url });
    }

    // Fetch session metadata (persona name → controls footer title).
    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        apiGetSession(token).then((result) => {
            if (cancelled) return;
            if (result?.persona?.name) setSessionTitle(result.persona.name);
            if (result?.session?.employee_email) setEmployeeEmail(result.session.employee_email);
            if (result?.campaign?.name) setCampaignName(result.campaign.name);
        });
        return () => {
            cancelled = true;
        };
    }, [token]);

    // Live timer — ticks while the conversation is connected. Stops on
    // disconnect / error so the footer freezes at the final elapsed value.
    useEffect(() => {
        if (phase !== "live") return;
        const id = window.setInterval(() => {
            setElapsedSeconds((s) => s + 1);
        }, 1000);
        return () => window.clearInterval(id);
    }, [phase]);

    const formattedTime = useMemo(() => {
        const m = Math.floor(elapsedSeconds / 60);
        const s = elapsedSeconds % 60;
        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }, [elapsedSeconds]);

    // Adapt internal transcript → TranscriptionPanel.Message shape.
    // Voice-only mode: only AI turns are surfaced in the panel. User audio is
    // captured by the bot for scoring but isn't echoed back as text.
    const transcriptionMessages = useMemo(
        () =>
            transcript
                .filter((m) => m.source === "ai" || m.source === "divider")
                .map((m, idx) => ({
                    id: `${idx}`,
                    role: m.source,
                    content: m.source === "divider" ? m.message : stripAudioTags(m.message),
                    timestamp: new Date(),
                    isComplete: true,
                })),
        [transcript]
    );

    if (phase === "feedback") {
        return (
            <div className="fixed inset-0 z-50 bg-white flex items-center justify-center p-6">
                <FeedbackPopup
                    mode="roleplay"
                    sessionToken={token}
                    interviewName={campaignName || sessionTitle || "Roleplay Session"}
                    userId={employeeEmail}
                    conversationId={conversationIdRef.current}
                    interviewStartTime={sessionStartTimeRef.current}
                    onComplete={() => navigate(`/training-results/${token}`, { replace: true })}
                />
            </div>
        );
    }

    return (
        <div
            className="fixed inset-0 z-50 bg-black"
            id="live-session-room"
        >
            <div
                id="ai-interview-container"
                className="w-full h-full bg-black overflow-hidden flex flex-col"
            >
                {/* Main Content Area — flex row */}
                <div className="flex-1 flex flex-row min-h-0 relative">
                    {/* Left column: Blob + Controls Footer */}
                    <div className="flex flex-col flex-1">
                        {/* "Video" area → Blob centered */}
                        <div className="relative flex-1 overflow-hidden bg-gray-900 flex items-center justify-center">
                            <ReactiveOrb
                                getVolume={
                                    phase === "live"
                                        ? conversation.isSpeaking
                                            ? () => Math.min(1, conversation.getOutputVolume() * 2.5)
                                            : () => Math.min(1, conversation.getInputVolume() * 2.5)
                                        : () => preflightVolumeRef.current
                                }
                                isActive={phase === "live"}
                                isSpeaking={conversation.isSpeaking}
                                sizeClassName="h-[min(17rem,62vw)] w-[min(17rem,62vw)]"
                            />

                            {/* Live status banner — same intent as VideoContainer's bar:
                                "AI speaking" while the bot has the floor, "your turn"
                                while it's listening. ElevenLabs doesn't expose a
                                distinct "thinking" state, so we never surface that. */}
                            {phase === "live" && (
                                <LiveStatusBar
                                    state={
                                        (conversation.isSpeaking
                                            ? "ai-speaking"
                                            : conversation.isListening
                                                ? "user-turn"
                                                : null) as LiveStatusState
                                    }
                                    aiName={sessionTitle || "ourteam AI"}
                                />
                            )}

                            {/* "You" label with live audio visualizer.
                                During live: shown when EL is listening (user's turn).
                                During preflight: shown only while recording the user's reply. */}
                            <div className="absolute bottom-2 left-2">
                                {(() => {
                                    const showBars = phase === "live" ? conversation.isListening : preflightRecording;
                                    return (
                                        <div className={`text-sm font-medium text-white bg-black/60 px-3 py-1.5 rounded-lg flex flex-row items-center ${showBars ? "gap-2" : ""}`}>
                                            You
                                            {showBars && (
                                                <EnhancedAudioVisualizer
                                                    isActive={true}
                                                    variant="bars"
                                                    color="green"
                                                    getVolume={phase === "live" ? conversation.getInputVolume : () => preflightVolumeRef.current}
                                                />
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Controls Footer */}
                        <div className="bg-gray-900 text-white p-3 flex justify-between items-center flex-shrink-0">
                            {/* Left — timer + roleplay title */}
                            <div className="flex-1 flex items-center gap-4 min-w-0">
                                <span className="text-sm font-mono text-gray-300 inline-flex items-center flex-shrink-0">
                                    <ClockIcon className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                                    {formattedTime}
                                </span>
                                {sessionTitle && (
                                    <>
                                        <div className="border-l border-gray-600 h-5 flex-shrink-0" />
                                        <div className="truncate text-sm" title={sessionTitle}>
                                            {sessionTitle}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Center — Settings + End buttons */}
                            <div className="flex-1 flex items-center justify-center gap-2 md:gap-3">
                                <button
                                    onClick={() => setShowSettings(true)}
                                    title="Device settings"
                                    aria-label="Device settings"
                                    className="p-2 md:p-3 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                                >
                                    <SettingsIcon className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={handleEnd}
                                    disabled={phase !== "live" || isEndingCall}
                                    title={isEndingCall ? "Ending session…" : "End"}
                                    aria-label="End session"
                                    aria-busy={isEndingCall}
                                    className="p-2 md:p-3 rounded-full bg-red-500 hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isEndingCall ? (
                                        <Loader2Icon className="h-5 w-5 animate-spin" />
                                    ) : (
                                        <PhoneIcon className="h-5 w-5" />
                                    )}
                                </button>
                            </div>

                            {/* Right — transcript toggle (desktop only) */}
                            <div className="hidden sm:flex flex-1 items-center gap-3 w-auto justify-end pl-2">
                                <TranscriptionToggleButton
                                    isOpen={showTranscript}
                                    onClick={() => setShowTranscript((v) => !v)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right column: Transcription Panel.
                        Kept mounted across hide/show toggles (controlled by
                        Tailwind `hidden`) so the panel's internal typing-animation
                        state is preserved — without this, every toggle replays
                        the typing intro for every prior message. */}
                    <div className={showTranscript ? "h-full" : "hidden"}>
                        <TranscriptionPanel
                            messages={transcriptionMessages}
                            aiName="ourteam AI"
                            aiSpeaking={conversation.isSpeaking}
                            userSpeaking={false}
                            aiThinking={waitingForFirstMessage}
                            className="border-l border-gray-700 h-full"
                            onClose={() => setShowTranscript(false)}
                        />
                    </div>
                </div>
            </div>

            <SettingsModal
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                onSave={({ microphone, speaker }) => {
                    if (microphone) {
                        setSelectedMicId(microphone);
                        conversation.changeInputDevice({ inputDeviceId: microphone }).catch(() => {});
                    }
                    if (speaker) {
                        setSelectedSpeakerId(speaker);
                        conversation.changeOutputDevice({ outputDeviceId: speaker }).catch(() => {});
                    }
                    setShowSettings(false);
                }}
                initialSelectedMicrophone={selectedMicId}
                initialSelectedSpeaker={selectedSpeakerId}
                hideCamera={true}
            />
        </div>
    );
}

function getPreflightAudioUrl(language?: string, gender?: string): string {
    const lang = (language ?? "en").toLowerCase();
    const prefix = lang.startsWith("id") ? "id"
        : lang.startsWith("ms") || lang.startsWith("my") ? "my"
        : lang.startsWith("vn") || lang.startsWith("vi") ? "vn"
        : "en";
    const g = gender === "male" || gender === "female" ? gender : "female";
    return `/audio/preflight-greeting-${prefix}-${g}.mp3`;
}

function getGreetingText(language?: string): string {
    const lang = (language ?? "en").toLowerCase();
    if (lang.startsWith("id")) return "Halo, apakah anda siap untuk role play hari ini?";
    if (lang.startsWith("ms") || lang.startsWith("my")) return "Helo, adakah anda bersedia untuk role play hari ini?";
    return "Hello, are you ready for your role-play today?";
}

function playAudioUrl(url: string, onVolume?: (v: number) => void): Promise<void> {
    return new Promise((resolve) => {
        const audio = new Audio(url);
        let ctx: AudioContext | null = null;
        let animating = false;

        if (onVolume) {
            ctx = new AudioContext();
            const source = ctx.createMediaElementSource(audio);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            analyser.connect(ctx.destination);
            const data = new Uint8Array(analyser.frequencyBinCount);
            animating = true;
            const tick = () => {
                if (!animating) return;
                analyser.getByteFrequencyData(data);
                const avg = data.reduce((s, v) => s + v, 0) / data.length;
                onVolume(Math.min(1, (avg / 255) * 2.5));
                requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        }

        const done = () => { animating = false; ctx?.close(); resolve(); };
        audio.onended = done;
        audio.onerror = done;
        audio.play().catch(done);
    });
}

function playRinging(): Promise<void> {
    return new Promise((resolve) => {
        const audio = new Audio("/audio/ringing.mp3");
        audio.onended = () => resolve();
        audio.onerror = () => resolve();
        audio.play().catch(() => resolve());
    });
}

function recordUntilSilence(
    stream: MediaStream,
    maxMs = 6000,
    onVolume?: (v: number) => void,
): Promise<{ blob: Blob; mimeType: string }> {
    return new Promise((resolve) => {
        const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
            ? "audio/webm;codecs=opus"
            : "audio/webm";
        const recorder = new MediaRecorder(stream, { mimeType });
        const chunks: BlobPart[] = [];
        recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
        recorder.onstop = () => {
            // globalThis.Blob is the native Blob (used here for the recording),
            // spelled explicitly to stay robust against any local `Blob` shadowing.
            const raw = new globalThis.Blob(chunks);
            resolve({ blob: raw.slice(0, raw.size, mimeType), mimeType });
        };

        const ctx = new AudioContext();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);

        let speechDetected = false;
        let silenceTimer: ReturnType<typeof setTimeout> | null = null;
        const maxTimer = setTimeout(stop, maxMs);

        function stop() {
            clearTimeout(maxTimer);
            if (silenceTimer) clearTimeout(silenceTimer);
            ctx.close();
            if (recorder.state === "recording") recorder.stop();
        }

        function tick() {
            if (recorder.state !== "recording") return;
            analyser.getByteFrequencyData(data);
            const avg = data.reduce((s, v) => s + v, 0) / data.length;
            onVolume?.(Math.min(1, (avg / 255) * 2.5));
            if (avg > 10) {
                speechDetected = true;
                if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
            } else if (speechDetected && !silenceTimer) {
                silenceTimer = setTimeout(stop, 800);
            }
            requestAnimationFrame(tick);
        }

        recorder.start();
        requestAnimationFrame(tick);
    });
}
